import { AppAuthError, AuthorizationNotifier, AuthorizationRequest, AuthorizationServiceConfiguration, BaseTokenRequestHandler, FetchRequestor, GRANT_TYPE_AUTHORIZATION_CODE, GRANT_TYPE_REFRESH_TOKEN, RedirectRequestHandler, TokenRequest } from '@openid/appauth';
import jwt_decode from 'jwt-decode';
import { catchError, finalize, forkJoin, from, map, mergeMap, Observable, of, Subject, tap, throwError } from 'rxjs';

export class ReleaseTrackerAuthnError extends Error {
    constructor(code, message) {
      super(message);
      this.code = code;
    }
}

// ReleaseTrackerAuthnService is a wrapper around the AppAuth library.
// It handles the authentication lifecycle.
export class ReleaseTrackerAuthnService {

    static get ID_TOKEN_HINT_STORAGE_KEY() {
        return "id_token_hint";
    }

    // Beware! the value of this attribute must be in sync with the name of
    // the parameter we are reading in oauth2redirect/index.html
    // var redirectURI = sessionStorage.getItem('redirect_to');
    static get REDIRECT_TO_STORAGE_KEY() {
        return 'redirect_to';
    }

    _redirectUri = "";
    _scope = 'openid offline profile email';

    _configuration = null; // AuthorizationServiceConfiguration
    _tokenResponse = null; // TokenResponse

    _authenticatedStatus = new Subject();
    authenticationChange = this._authenticatedStatus.asObservable();
    _refreshTokenObservable; // Observable<string>

    tokenHandler; // BaseTokenRequestHandler
    authzReqHandler; // RedirectRequestHandler
    notifier; // AuthorizationNotifier

    constructor(history) {
        this._history = history;

        this._openIdHost = process.env.REACT_APP_OIDC_HOST;
        this._clientId = process.env.REACT_APP_OIDC_CLIENT_ID;

        const origin = window.location.origin;
        this._redirectUri = `${origin}/oauth2redirect`;

        this.tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
        this.authzReqHandler = new RedirectRequestHandler();
        this.notifier = new AuthorizationNotifier();
        this.authzReqHandler.setAuthorizationNotifier(this.notifier);
        }

    isAuthenticated() {
        return Boolean(this._tokenResponse);
    }

    isAuthenticating() {
        return window.location.hash.includes('#code') ||
            this._hasOIDCFlowFailed();
    }

    _hasOIDCFlowFailed() {
        return window.location.hash.includes('#error');
    }

    startAuthnFlow(redirectTo, token) {
        return this._updateServiceConfiguration().pipe(
            catchError(() => {
                return throwError(() => new ReleaseTrackerAuthnError(505, 'Could not get the OIDC server configuration'));
            }),
            tap(() => {
                this._makeAuthorizationRequest(redirectTo, token);
            }),
        );
    }


    getAccessToken() {
        return of(this.isAuthenticated()).pipe(
            mergeMap((status) => {
                if (!status) {
                    return throwError(() => new Error('user is not authenticated'));
                }
                if (new Date() >= new Date((this._tokenResponse.issuedAt + this._tokenResponse.expiresIn) * 1000)) {
                    return this._refreshToken();
                }
                return of(this._tokenResponse.accessToken);
            }),
        );
    }

    completeAuthnProcess() {
        if (this._hasOIDCFlowFailed()) {
            // If we've tried to sign-in with prompt='none' and the request has been rejected,
            // let's try again but with prompt='consent'
            if (window.localStorage.getItem(ReleaseTrackerAuthnService.ID_TOKEN_HINT_STORAGE_KEY)) {
                window.localStorage.removeItem(ReleaseTrackerAuthnService.ID_TOKEN_HINT_STORAGE_KEY);
                return this.startAuthnFlow(window.location.pathname + window.location.search);
            }
            return throwError(() => new ReleaseTrackerAuthnError(401, 'OIDC Flow failed'));
        }
        return this.makeTokenRequest();
    }

    makeTokenRequest() {
        if (!this.isAuthenticating()) {
            return throwError(() => new ReleaseTrackerAuthnError(401, 'Authentication code is missing'));
        }
        if (this._hasOIDCFlowFailed()) {
            return throwError(() => new ReleaseTrackerAuthnError(401, 'OIDC Flow failed'));
        }
        return this._updateServiceConfiguration().pipe(
            catchError(() => {
                return throwError(() => new ReleaseTrackerAuthnError(505, 'Could not get the OIDC server configuration'));
            }),
            mergeMap(() => forkJoin([
                this._setAuthorizationListenerNotifier(),
                from(this.authzReqHandler.completeAuthorizationRequestIfPossible()).pipe(
                    catchError(() => {
                        return throwError(() => new ReleaseTrackerAuthnError(505, 'An error occurred completing the authorization request'));
                    }),
                ),
            ])),
            map(() => true),
        );
    }

    getCurrentUser() {
        let result = '';
        if (this._tokenResponse) {
            const payload = jwt_decode(this._tokenResponse.idToken);
            result = payload ? payload.sub : '';
        }
        return result;
    }

    _refreshToken() {
        if (!this._refreshTokenObservable) {
            const request = new TokenRequest({
                client_id: this._clientId,
                redirect_uri: this._redirectUri,
                grant_type: GRANT_TYPE_REFRESH_TOKEN,
                code: undefined,
                refresh_token: this._tokenResponse.refreshToken,
                extras: undefined,
            });
            this._refreshTokenObservable = from(this.tokenHandler.performTokenRequest(this._configuration, request)).pipe(
                catchError((error) => {
                    if (error instanceof AppAuthError && error.message === '400') {
                        // refresh token expired
                        return this.startAuthnFlow(window.location.pathname + window.location.search, undefined).pipe(
                            mergeMap(() => throwError(() => error)),
                        );
                    }
                    return throwError(() => error);
                }),
                tap((tokenResponse) => {
                    this._tokenResponse.accessToken = tokenResponse.accessToken;
                    this._tokenResponse.refreshToken = tokenResponse.refreshToken;
                    this._tokenResponse.issuedAt = tokenResponse.issuedAt;
                    this._tokenResponse.expiresIn = tokenResponse.expiresIn;
                    this._tokenResponse.tokenType = tokenResponse.tokenType;
                    this._tokenResponse.scope = tokenResponse.scope;
                }),
                finalize(() => {
                    this._refreshTokenObservable = undefined;
                }),
                map((tokenResponse) => tokenResponse.accessToken),
            );
        }
        return this._refreshTokenObservable;
    }

    _setAuthorizationListenerNotifier() {
        return new Observable((observer) => {
            this.notifier.setAuthorizationListener((req, res, error) => {
                if (error) {
                    observer.error('An error occurred getting the access token');
                } else if (res) {
                    this._getToken(req, res.code).subscribe({
                        next: () => {
                            this._cleanURL();
                            observer.next(true);
                            observer.complete();
                        },
                        error: (err) => {
                            console.error(err);
                            observer.error('Could not get the token');
                        },
                    }).add(() => {
                        sessionStorage.removeItem(ReleaseTrackerAuthnService.REDIRECT_TO_STORAGE_KEY);
                    });
                }
            });
        });
    }

    // Removes all OIDC flow related hash params from the URL.
    _cleanURL() {
        if (window.location.hash !== "") {
            this._history.replace(window.location.pathname);
        }
    }

    _getToken(request, code) {
        const extras = {};
        extras.code_verifier = request.internal.code_verifier;
        // use the code to make the token request.
        const tokenRequest = new TokenRequest({
            client_id: this._clientId,
            redirect_uri: this._redirectUri,
            grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
            code,
            refresh_token: undefined,
            extras,
        });
        return from(this.tokenHandler.performTokenRequest(this._configuration, tokenRequest)).pipe(
            tap((response) => {
                this._tokenResponse = response;
                this._authenticatedStatus.next(Boolean(this._tokenResponse));
                window.localStorage.setItem(ReleaseTrackerAuthnService.ID_TOKEN_HINT_STORAGE_KEY, encodeURIComponent(response.idToken));
            }),
            map(() => void 0),
        );
    }

    _updateServiceConfiguration() {
        if (this._configuration) {
            return of(true);
        }
        return from(AuthorizationServiceConfiguration.fetchFromIssuer(
            this._openIdHost,
            new FetchRequestor(),
        )).pipe(
            tap((response) => {
                this._configuration = response;
            }),
            map(() => true),
        );
    }

    _makeAuthorizationRequest(redirectTo, token) {
        const request = new AuthorizationRequest({
            client_id: this._clientId,
            redirect_uri: this._redirectUri,
            scope: this._scope,
            response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
            extras: { prompt: 'consent', access_type: 'offline' },
        });

        sessionStorage.setItem(ReleaseTrackerAuthnService.REDIRECT_TO_STORAGE_KEY, redirectTo);

        if (token) {
            request.extras.token = token;
        } else {
            const idTokenHint = window.localStorage.getItem(ReleaseTrackerAuthnService.ID_TOKEN_HINT_STORAGE_KEY);
            // if the user has been signed-in previously, try to skip the login screen
            if (idTokenHint) {
                request.extras.prompt = 'none';
                request.extras.id_token_hint = idTokenHint;
            }
        }

        request.setupCodeVerifier().then(() => {
            this.authzReqHandler.performAuthorizationRequest(
                this._configuration,
                request,
            );
        });
    }

    logout() {
        this.revokeTokens();
        const idToken = this._tokenResponse.idToken;
        delete this._tokenResponse;
        this._authenticatedStatus.next(Boolean(this._tokenResponse));
        window.localStorage.removeItem(ReleaseTrackerAuthnService.ID_TOKEN_HINT_STORAGE_KEY);
        const query = `id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${window.location.origin}`;
        window.location.replace(`${this._configuration.endSessionEndpoint}?${query}`);
    }

    revokeTokens() {
        of(this.isAuthenticated()).subscribe((status) => {
            if (status && navigator.sendBeacon) {
                const atrData = new FormData();
                atrData.append('client_id', this._clientId);
                atrData.append('token', this._tokenResponse.accessToken);
                atrData.append('token_type_hint', 'access_token');

                const rtrData = new FormData();
                rtrData.append('client_id', this._clientId);
                rtrData.append('token', this._tokenResponse.refreshToken);
                rtrData.append('token_type_hint', 'refresh_token');

                navigator.sendBeacon(this._configuration.revocationEndpoint, atrData);
                navigator.sendBeacon(this._configuration.revocationEndpoint, rtrData);
            }
        });
    }
}
